import { useState, useEffect, useRef } from 'react';
import type { Drill, Practice } from '../types';

// Define audio files for warning and drill end sounds
const AUDIO_FILES = [
  { label: 'Referee Whistle', file: '/assets/audio/referee-whistle-blow-gymnasium-6320.mp3' },
  { label: 'Generic Alarm Clock', file: '/assets/audio/generic-alarm-clock-86759.mp3' },
  { label: 'Clock Alarm', file: '/assets/audio/clock-alarm-8761.mp3' },
  { label: 'Notification Alert 7', file: '/assets/audio/notification-alert-7-331719.mp3' },
  { label: 'Notification Alert 4', file: '/assets/audio/notification-alert-4-331722.mp3' },
  { label: 'Notification Ping', file: '/assets/audio/notification-ping-335500.mp3' },
  { label: 'Air Horn', file: '/assets/audio/air-horn-273892.mp3' },
];

interface UseTimersProps {
  drills: Drill[];
  selectedPractice: Practice | null;
}

export function useTimers({ drills, selectedPractice }: UseTimersProps) {
  // Timer and warning state
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [lastPlayedDrillIndex, setLastPlayedDrillIndex] = useState<number | null>(null);
  const [lastWarningDrillIndex, setLastWarningDrillIndex] = useState<number | null>(null);
  const [currentDrillIndex, setCurrentDrillIndex] = useState<number | null>(null);
  const [warningDrillIndex, setWarningDrillIndex] = useState<number | null>(null);
  
  // Sound settings
  const [warningInterval, setWarningInterval] = useState(() => 
    parseInt(localStorage.getItem('warningInterval') || '2', 10)
  );
  const [warningSound, setWarningSound] = useState(() => 
    localStorage.getItem('warningSound') || AUDIO_FILES[3].file
  );
  const [drillEndSound, setDrillEndSound] = useState(() => 
    localStorage.getItem('drillEndSound') || AUDIO_FILES[6].file
  );
  const [isMuted, setIsMuted] = useState(() => 
    localStorage.getItem('drillSoundMuted') === 'true'
  );

  // Audio elements
  const warningAudioRef = useRef<HTMLAudioElement | null>(null);
  const drillEndAudioRef = useRef<HTMLAudioElement | null>(null);

  // Previous drill index for detecting changes
  const prevDrillIndexRef = useRef<number | null>(null);

  // Update current time every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  // Initialize audio refs
  useEffect(() => {
    if (typeof window !== 'undefined') {
      warningAudioRef.current = new Audio(warningSound);
      drillEndAudioRef.current = new Audio(drillEndSound);
    }
  }, [warningSound, drillEndSound]);

  // Play warning sound
  const playWarningSound = () => {
    if (warningAudioRef.current && !isMuted) {
      try {
        // Add cache-busting parameter to prevent cache issues
        const cacheBuster = `?t=${Date.now()}`;
        warningAudioRef.current.src = warningSound + cacheBuster;
        warningAudioRef.current.currentTime = 0;
        warningAudioRef.current.load(); // Force reload
        warningAudioRef.current.play().catch(e => {
          console.error('Error playing warning sound:', e);
          // Fallback: try without cache buster
          warningAudioRef.current.src = warningSound;
          warningAudioRef.current.play().catch(fallbackError => {
            console.error('Fallback audio play failed:', fallbackError);
          });
        });
      } catch (error) {
        console.error('Error setting up warning sound:', error);
      }
    }
  };

  // Play drill end sound
  const playDrillEndSound = () => {
    if (drillEndAudioRef.current && !isMuted) {
      try {
        // Add cache-busting parameter to prevent cache issues
        const cacheBuster = `?t=${Date.now()}`;
        drillEndAudioRef.current.src = drillEndSound + cacheBuster;
        drillEndAudioRef.current.currentTime = 0;
        drillEndAudioRef.current.load(); // Force reload
        drillEndAudioRef.current.play().catch(e => {
          console.error('Error playing drill end sound:', e);
          // Fallback: try without cache buster
          drillEndAudioRef.current.src = drillEndSound;
          drillEndAudioRef.current.play().catch(fallbackError => {
            console.error('Fallback audio play failed:', fallbackError);
          });
        });
      } catch (error) {
        console.error('Error setting up drill end sound:', error);
      }
    }
  };

  // Get minutes since practice start
  const getMinutesSincePracticeStart = () => {
    if (!selectedPractice?.start_time) return null;
    
    try {
      const [h, m] = selectedPractice.start_time.split(':').map(Number);
      const practiceStart = new Date();
      practiceStart.setHours(h, m, 0, 0);
      const now = new Date(currentTime);
      return Math.floor((now.getTime() - practiceStart.getTime()) / 60000);
    } catch (error) {
      console.error('Error calculating minutes since practice start:', error);
      return null;
    }
  };

  // Calculate current and warning drill indexes
  useEffect(() => {
    const minutesSinceStart = getMinutesSincePracticeStart();
    
    // Reset indices if no minutes or no drills
    if (minutesSinceStart === null || drills.length === 0) {
      setCurrentDrillIndex(null);
      setWarningDrillIndex(null);
      return;
    }

    // Find current drill based on time
    let current = null;
    let warning = null;
    let accumulated = 0;
    
    for (let i = 0; i < drills.length; i++) {
      const start = accumulated;
      const end = accumulated + drills[i].duration;
      
      if (minutesSinceStart >= start && minutesSinceStart < end) {
        current = i;
        // Set warning if we're near the end of this drill
        if (end - minutesSinceStart <= warningInterval) {
          warning = i;
        }
        break;
      }
      accumulated = end;
    }

    setCurrentDrillIndex(current);
    setWarningDrillIndex(warning);
  }, [currentTime, drills, selectedPractice, warningInterval]);

  // Handle sound effects for drill transitions and warnings
  useEffect(() => {
    if (isMuted || drills.length === 0) return;
    
    // Play warning sound
    if (
      warningDrillIndex !== null &&
      lastWarningDrillIndex !== warningDrillIndex &&
      warningDrillIndex === currentDrillIndex
    ) {
      playWarningSound();
      setLastWarningDrillIndex(warningDrillIndex);
    }
    
    // Play drill end sound when drill index advances
    if (
      prevDrillIndexRef.current !== null &&
      prevDrillIndexRef.current !== currentDrillIndex &&
      prevDrillIndexRef.current !== -1
    ) {
      playDrillEndSound();
      setLastPlayedDrillIndex(prevDrillIndexRef.current);
    }
    
    prevDrillIndexRef.current = currentDrillIndex;
  }, [currentDrillIndex, warningDrillIndex, isMuted, drills]);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('warningInterval', String(warningInterval));
    localStorage.setItem('warningSound', warningSound);
    localStorage.setItem('drillEndSound', drillEndSound);
    localStorage.setItem('drillSoundMuted', String(isMuted));
  }, [warningInterval, warningSound, drillEndSound, isMuted]);

  // Sync with localStorage on changes
  useEffect(() => {
    const syncSettings = () => {
      setWarningInterval(parseInt(localStorage.getItem('warningInterval') || '2', 10));
      setWarningSound(localStorage.getItem('warningSound') || AUDIO_FILES[3].file);
      setDrillEndSound(localStorage.getItem('drillEndSound') || AUDIO_FILES[6].file);
      setIsMuted(localStorage.getItem('drillSoundMuted') === 'true');
    };
    
    window.addEventListener('storage', syncSettings);
    return () => window.removeEventListener('storage', syncSettings);
  }, []);

  // Handle changing the warning interval
  const handleWarningIntervalChange = (value: number) => {
    setWarningInterval(value);
    localStorage.setItem('warningInterval', String(value));
  };

  // Handle changing the warning sound
  const handleWarningSoundChange = (file: string) => {
    setWarningSound(file);
    localStorage.setItem('warningSound', file);
  };

  // Handle changing the drill end sound
  const handleDrillEndSoundChange = (file: string) => {
    setDrillEndSound(file);
    localStorage.setItem('drillEndSound', file);
  };

  // Toggle mute
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    localStorage.setItem('drillSoundMuted', String(newMuted));
  };

  return {
    currentDrillIndex,
    warningDrillIndex,
    warningInterval,
    warningSound,
    drillEndSound,
    isMuted,
    availableAudioFiles: AUDIO_FILES,
    handleWarningIntervalChange,
    handleWarningSoundChange,
    handleDrillEndSoundChange,
    toggleMute
  };
} 